import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FormData, CalculationResult, calculateLifeYears, validateInput } from '../utils/calculator';
import OptionButton from './OptionButton';
import AgeSlider from './AgeSlider';
import InputField from './InputField';
import ResultsPage from './ResultsPage';
import BackArrowIcon from './BackArrowIcon';
import Footer from './Footer';
import { getFirestore, collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
// Removed Firestore imports for addResultToFirebase
import { useAuth } from '../contexts/AuthContext';
const CalculatorPage: React.FC = () => {
    const { user, signOut } = useAuth();
    const [formData, setFormData] = useState<FormData>({
        gender: 'female',
        age: 60,
        smoking: 'no',
        systolic: 0,
        cholesterol: 0,
    });
    // Removed firebaseDocId state


    // addResult();

    const [results, setResults] = useState<CalculationResult | null>(null);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [showResultsPage, setShowResultsPage] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [pilotCode, setPilotCode] = useState<string>('');
    const [resultIdentifier, setResultIdentifier] = useState<string | null>(null);
    const handleGenderChange = (value: string) => {
        setFormData(prev => ({ ...prev, gender: value as 'female' | 'male' }));
    };

    const handleAgeChange = (age: number) => {
        setFormData(prev => ({ ...prev, age }));
    };

    const handleSmokingChange = (value: string) => {
        setFormData(prev => ({ ...prev, smoking: value as 'yes' | 'no' }));
    };

    const handleSystolicChange = (value: number) => {
        setFormData(prev => ({ ...prev, systolic: value }));
    };

    const handleCholesterolChange = (value: number) => {
        setFormData(prev => ({ ...prev, cholesterol: value }));
    };

    const handlePilotCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPilotCode(e.target.value);
    };
    // const db = getFirestore(); // Initialize Firestore

    // const lifePlusData = {
    //   input: {
    //     gender: "male",
    //     age: 58,
    //     smoking: "no",
    //     systolic: 150,
    //     cholesterol: 150
    //   },
    //   result: {
    //     smokeYears: 3.2,
    //     systolicYears: 2.8,
    //     cholesterolYears: 1.3250000000000002
    //   }
    // };
    // Utility to ensure anonymous auth and wait for ID token
    async function ensureAnonymousAuth(auth: ReturnType<typeof getAuth>) {
        if (!auth.currentUser) {
            await signInAnonymously(auth);
        }
        // Wait for the ID token to be available
        await new Promise<void>((resolve) => {
            const unsubscribe = auth.onAuthStateChanged((user) => {
                if (user) {
                    resolve();
                    unsubscribe();
                }
            });
        });
    }

    // Add result to Firestore only after name and email are entered
    async function addResultToFirebase(result: CalculationResult) {
        try {
            // There's also a pilotCode validation in the server but this is just in case for reduced writes/invocations to Firestore
            if (result.pilotCode !== '90210') {
                return;
            }
            setIsLoading(true);
            // Ensure user is authenticated anonymously and token is ready
            let auth = getAuth();
            await ensureAnonymousAuth(auth);
            auth = getAuth();
            const db = getFirestore();
            // Get the last item in the collection sorted by createdAt
            //-------
            const functions = getFunctions(app, 'us-central1'); // <--- Change 'me-west1' to your function's actual region if different
            const getNextIdentifier = httpsCallable(functions, 'getNextResultIdentifierCallable');
            const identifierResult = await getNextIdentifier({}) as any;
            //-------

            // const lastItemIdentifier = lastItem?.resultIdentifier || 'LP000';
            const nextIdentifier = identifierResult?.data?.nextIdentifier || 'LP000';
            const newIdentifierDisplay = `LP${String(nextIdentifier).padStart(3, '0')}`;
            setResultIdentifier(newIdentifierDisplay);
            // Save to Firestore
            // Ensure again before writing (should be redundant, but safe)
            await ensureAnonymousAuth(auth);
            const docRef = await addDoc(collection(db, "life-plus-results"), {
                ...result,
                resultIdentifier: nextIdentifier,
                resultIdentifierDisplay: newIdentifierDisplay,
                createdAt: serverTimestamp()
            });
            setIsLoading(false);
            setResults({
                ...result,
                resultIdentifier: nextIdentifier,
                resultIdentifierDisplay: newIdentifierDisplay,
            })
            return docRef.id;
        } catch (e) {
            console.error("Error adding document: ", e);
        }
    }
    const handleCalculate = async () => {
        setIsLoading(true);
        setError('');
        setResults(null);
        setShowValidation(true); // Show validation errors after calculation attempt

        try {
            // Validate form data
            if (!validateInput(
                formData.gender,
                formData.age,
                formData.smoking,
                formData.systolic,
                formData.cholesterol
            )) {
                setError('אנא מלא את כל השדות הנדרשים');
                return;
            }

            // Calculate results
            const result = calculateLifeYears(
                formData.gender,
                formData.age,
                formData.smoking,
                formData.systolic,
                formData.cholesterol
            );
            result.pilotCode = pilotCode;
            if (result.success) {
                setResults(result);
                setError('');
                await addResultToFirebase(result);
                setShowResultsPage(true);
            } else {
                setError('שגיאה בחישוב: ' + (result.error || 'שגיאה לא ידועה'));
                setResults(null);
                setShowResultsPage(false);
            }
        } catch (error) {
            console.error('Calculation error:', error);
            setError('שגיאה בחישוב');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackFromResults = () => {
        setShowResultsPage(false);
    };

    useEffect(() => {
        document.title = 'מחשבון הוספת שנות חיים - חיים פלוס';
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    // Show results page if results are available
    if (showResultsPage && results) {
        return <ResultsPage results={results} onBack={handleBackFromResults} />;
    }

    return (
        <div className="calculator-page">
            <a href="#main-content" className="skip-link">דלג לתוכן הראשי</a>
            <div className="container">
                {/* Background decorative elements */}
                {/* <div className="bg-image bg-image-1"></div>
                <div className="bg-image bg-image-2"></div> */}


                {/* Back to Home Button */}
                <div className="back-home">
                    <Link to="/" className="back-btn" title="חזרה לדף הבית" aria-label="חזרה לדף הבית">
                        <BackArrowIcon aria-hidden="true" />
                    </Link>
                </div>

                {/* Header */}
                <div className="calculator-header">
                    <div className="logo">
                        <img
                            src="/assets/life-logo.svg"
                            alt="חיים פלוס"
                        />
                    </div>
                    <img
                        src="/assets/header-img.png"
                        alt=""
                        className="header-image"
                        aria-hidden="true"
                    />
                </div>

                {/* Main Calculator Card */}
                <main id="main-content">
                    <div className="calculator-card">
                        {/* Header */}
                        <div className="card-header">
                            <h1>מחשבון הוספת שנות חיים</h1>
                        </div>

                        {/* Content */}
                        <div className="card-content">
                            {/* Error Message */}
                            {error && (
                                <div className="error-message" role="alert" aria-live="assertive">
                                    {error}
                                </div>
                            )}
                            {/* ARIA live region for loading and status updates */}
                            <div aria-live="polite" aria-atomic="true" className="sr-only">
                                {isLoading && 'מחשב תוצאות, אנא המתן'}
                            </div>

                            {/* Gender Selection */}
                            <div className="form-group">
                                <label className="form-label">המין שלך</label>
                                <div className="button-group">
                                    <OptionButton
                                        value="female"
                                        label="אשה"
                                        isActive={formData.gender === 'female'}
                                        onClick={handleGenderChange}
                                    />
                                    <OptionButton
                                        value="male"
                                        label="גבר"
                                        isActive={formData.gender === 'male'}
                                        onClick={handleGenderChange}
                                    />
                                </div>
                            </div>

                            {/* Age Selection */}
                            <div className="form-group age-group">
                                <label className="form-label" id="age-label">הגיל שלך</label>
                                <AgeSlider value={formData.age} onChange={handleAgeChange} aria-labelledby="age-label" />
                            </div>

                            {/* Smoking Question */}
                            <div className="form-group">
                                <label className="form-label">
                                    {formData.gender === 'male' ? 'האם אתה מעשן' : 'האם את מעשנת'}
                                </label>
                                <div className="button-group">
                                    <OptionButton
                                        value="no"
                                        label="לא"
                                        isActive={formData.smoking === 'no'}
                                        onClick={handleSmokingChange}
                                    />
                                    <OptionButton
                                        value="yes"
                                        label="כן"
                                        isActive={formData.smoking === 'yes'}
                                        onClick={handleSmokingChange}
                                    />
                                </div>
                            </div>

                            {/* Blood Pressure */}
                            <InputField
                                value={formData.systolic}
                                onChange={handleSystolicChange}
                                placeholder=""
                                label="לחץ דם סיסטולי"
                                infoText="בקשו מהרופא/ה"
                                min={80}
                                max={300}
                                errorMessage="הערך חייב להיות בין 80-300"
                                showValidation={showValidation}
                            />

                            {/* Cholesterol */}
                            <InputField
                                value={formData.cholesterol}
                                onChange={handleCholesterolChange}
                                placeholder=""
                                label="כולסטרול (Non-HDL)"
                                infoText="בקשו מהרופא/ה"
                                min={50}
                                max={500}
                                errorMessage="הערך חייב להיות בין 50-500"
                                showValidation={showValidation}
                            />

                            {/* Calculate Button */}
                            <button
                                className={`calculate-btn ${isLoading ? 'loading' : ''}`}
                                onClick={handleCalculate}
                                disabled={isLoading}
                                aria-busy={isLoading}
                                aria-disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="loading-spinner" aria-hidden="true"></span>
                                        מחשב...
                                    </>
                                ) : (
                                    'חישוב תוצאה'
                                )}
                            </button>
                        </div>
                    </div>
                </main>

                {/* Pilot Code Card */}
                <div className="calculator-card">
                    <div className="card-content">
                        <div className="form-group">
                            <div className="input-header">
                                <span className="info-text">לשימוש המרפאה</span>
                                <div className="info-dot" aria-hidden="true"></div>
                                <label className="form-label" htmlFor="pilot-code-input">
                                    קוד פיילוט
                                </label>
                            </div>
                            <div className="input-field">
                                <input
                                    id="pilot-code-input"
                                    type="text"
                                    value={pilotCode}
                                    onChange={handlePilotCodeChange}
                                    placeholder=""
                                    className=""
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Disclaimer */}
                <Footer />
            </div>
        </div>
    );
};

export default CalculatorPage;
